import { scheduleDateAtom } from '@/atoms/sessionAtoms';
import { useIsProblemBasedChartingEnabled } from '@/features/flags';
import { useBuildSessionPath } from '@/features/sessions/utils/buildSessionPath';
import { getCurrentRouteSessionId } from '@/features/sessions/utils/getCurrentRouteSessionId';
import { useIntegrationEhrInfo } from '@/hooks/integrations/useIntegrationEhrInfo';
import { useMutateUpdateSessionEHRAppointmentId } from '@/hooks/mutation/integration/useMutateUpdateSessionEHRAppointmentId';
import { useMutateCreateNewSession } from '@/hooks/mutation/useMutateCreateNewSession';
import { useMutateCreateAndLinkPatientProfile } from '@/hooks/mutation/useMutateCreatePatientProfile';
import { useMutateLinkSessionsToPatientProfile } from '@/hooks/mutation/useMutateLinkSessionsToPatientProfile';
import { useQueryPatientProfiles } from '@/hooks/query/useQueryPatientProfiles';
import { trackPatientSelected } from '@/lib/analytics/scribe/patient';
import { TIME_FORMAT } from '@/utils/integration/parse-ehr-appt-time';
import { logger } from '@/utils/logger/logger';
import { type EHRAppointment } from '@heidi/integrations/ehr/contracts';
import { format } from 'date-fns';
import { useAtomValue } from 'jotai';
import { useRouter } from 'next/router';
import { useThrottledCallback } from 'use-debounce';

export const useLinkEhrPatientToSession = () => {
  const router = useRouter();
  const scheduleDate = useAtomValue(scheduleDateAtom);
  const { integrationEhrProvider } = useIntegrationEhrInfo();
  const { data: allPatientProfiles } = useQueryPatientProfiles();

  const { mutateAsync: createNewSession, isPending: isCreateNewSessionPending } =
    useMutateCreateNewSession({
      navigate: false,
    });
  const {
    mutateAsync: createAndLinkPatientProfile,
    isPending: isCreateAndLinkPatientProfilePending,
  } = useMutateCreateAndLinkPatientProfile();
  const {
    mutateAsync: linkSessionsToPatientProfile,
    isPending: isLinkSessionsToPatientProfilePending,
  } = useMutateLinkSessionsToPatientProfile();
  const {
    mutateAsync: updateSessionEHRApptID,
    isPending: isUpdateSessionEHRApptIDPending,
  } = useMutateUpdateSessionEHRAppointmentId(scheduleDate);
  // Hook, not the imperative getter: the session is stamped once, at creation.
  // A pre-hydration `false` here does not just skip a render - it writes a
  // session that can never resolve a patient for a later code push.
  const problemBasedCharting = useIsProblemBasedChartingEnabled();
  const buildSessionPath = useBuildSessionPath();
  const currentSessionId = getCurrentRouteSessionId(router);

  const isPending =
    isCreateNewSessionPending ||
    isCreateAndLinkPatientProfilePending ||
    isLinkSessionsToPatientProfilePending ||
    isUpdateSessionEHRApptIDPending;

  const onLinkEhrPatientToSession = useThrottledCallback(async (appt: EHRAppointment) => {
    // check if session already exists, if exists, redirect to that session
    if (appt.session_id) {
      if (appt.session_id === currentSessionId) return;
      void router.push(buildSessionPath(appt.session_id));
      return;
    }

    try {
      // check if patient profile already exists
      const ehrPatientProfile = allPatientProfiles?.find(
        (profile) => profile.ehr_patient_id === appt.patient_id
      );

      // create new session and get session id
      const { data } = await createNewSession();
      // Early return when new session is not created
      if (!data) {
        logger.error('Failed to create new session - no data returned');
        return;
      }

      const sessionId = data.session_id;
      const createdTimeLocal =
        appt.appointment_date_time || format(new Date(), TIME_FORMAT);

      // Wait for EHR appointment ID update to complete
      await updateSessionEHRApptID({
        sessionId,
        EHRApptId: appt.record_id,
        createdTimeLocal,
        ehrProvider: integrationEhrProvider,
        ...(problemBasedCharting ? { ehrPatientId: appt.patient_id } : {}),
      });

      if (!ehrPatientProfile) {
        // if not, create and link patient profile
        await createAndLinkPatientProfile({
          patientProfile: {
            ehr_patient_id: appt.patient_id,
            patient_sex_code: appt.patient_sex_code,
            first_name: appt.patient_first_name,
            last_name: appt.patient_last_name,
          },
          sessionIds: [sessionId],
        });
      } else {
        // if exists, link session to patient profile
        await linkSessionsToPatientProfile({
          patientProfile: ehrPatientProfile,
          sessionIds: [sessionId],
        });
      }

      // Track analytics after successful EHR update
      trackPatientSelected({
        patient_id: appt.patient_id,
        session_id: sessionId,
        source: 'ehr_integration',
      });
      // Navigate to the session page after all operations are complete
      void router.push(buildSessionPath(sessionId));
    } catch (error) {
      logger.error('Error in handleSessionStart:', error);
    }
  }, 2000);

  return {
    isPending,
    onLinkEhrPatientToSession,
  };
};
